import { Logger } from '@core/model/domain/logger';
import { environment } from '@environments/environment';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { injectable } from 'inversify';

@injectable()
export class AppInsightsLogger implements Logger {
  appInsights: ApplicationInsights;

  constructor() {
    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: environment.appInsights.instrumentationKey,
        enableAutoRouteTracking: false, // option to log all route changes
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxErrorStatusText: true,
        enableUnhandledPromiseRejectionTracking: true,
        excludeRequestFromAutoTrackingPatterns: [/^http.*kameleoon.io.*$/g, /^http.*trustcommander.net.*$/g],
        enableCorsCorrelation: true,
        isStorageUseDisabled: true,
        maxBatchInterval: 0,
      },
    });

    this.appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
      envelope.tags = envelope.tags || [];
      envelope.tags.push({ 'ai.cloud.role': 'Frontend - Open Banking' });
    });

    this.appInsights.loadAppInsights();
  }

  logPageView(name?: string, url?: string) {
    this.appInsights.trackPageView({
      name: name,
      uri: url,
    });
  }

  startTrackPage(pageName: string) {
    this.appInsights.startTrackPage(pageName);
  }

  stopTrackPage(pageName: string) {
    this.appInsights.stopTrackPage(pageName);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logEvent(name: string, properties?: { [key: string]: any }) {
    this.appInsights.trackEvent({ name: name }, properties);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logMetric(name: string, average: number, properties?: { [key: string]: any }) {
    this.appInsights.trackMetric({ name: name, average: average }, properties);
  }

  logException(exception: Error, severityLevel?: number) {
    this.appInsights.trackException({ exception: exception, severityLevel: severityLevel });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logTrace(message: string, properties?: { [key: string]: any }) {
    this.appInsights.trackTrace({ message: message }, properties);
  }
}
